import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import Button from '../components/ui/Button';
import { getImageUrl } from '../utils/imageUtils';

const MesProduits = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    prix: '',
    categorie: '',
    materiaux: '',
    dimensions: { longueur: '', largeur: '', hauteur: '', unite: 'cm' },
    pourcentageReduction: 0,
  });
  const [photos, setPhotos] = useState([]);
  const [photosPreviews, setPhotosPreviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const categories = [
    'Textile et Tissage',
    'Poterie et Céramique',
    'Sculpture sur Bois',
    'Maroquinerie',
    'Bijouterie Traditionnelle',
    'Vannerie',
    'Ferronnerie',
    'Peinture et Art',
    'Instruments de Musique',
    'Décoration',
    'Autre'
  ];

  const fetchProduits = useCallback(async () => {
    if (!isAuthenticated || !user?._id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Utiliser l'endpoint spécifique pour mes produits (retourne tous les produits sans filtres)
      const res = await axios.get(`/produits/mes-produits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProduits(res.data.produits || []);
    } catch (err) {
      console.error('Erreur chargement produits:', err);
      setError('Erreur lors du chargement de vos produits');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?._id]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchProduits();
    }
  }, [isAuthenticated, user, fetchProduits]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('dimensions.')) {
      const key = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        dimensions: { ...prev.dimensions, [key]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photos.length > 5) {
      setError('Maximum 5 photos autorisées');
      return;
    }

    setPhotos(prev => [...prev, ...files]);
    
    const previews = files.map(file => URL.createObjectURL(file));
    setPhotosPreviews(prev => [...prev, ...previews]);
    setError('');
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotosPreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      prix: '',
      categorie: '',
      materiaux: '',
      dimensions: { longueur: '', largeur: '', hauteur: '', unite: 'cm' },
      pourcentageReduction: 0,
    });
    setPhotos([]);
    setPhotosPreviews([]);
    setEditingProduct(null);
    setShowForm(false);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validation côté client
      if (!editingProduct && photos.length === 0) {
        setSaving(false);
        return setError('Ajoutez au moins une photo du produit.');
      }
      if (!formData.nom || !formData.description || !formData.prix || !formData.categorie) {
        setSaving(false);
        return setError('Veuillez remplir les champs obligatoires: nom, description, prix, catégorie.');
      }
      const prixNumber = Number(formData.prix);
      if (isNaN(prixNumber) || prixNumber < 0) {
        setSaving(false);
        return setError('Le prix doit être un nombre positif.');
      }
      const reduc = Number(formData.pourcentageReduction || 0);
      if (reduc < 0 || reduc > 99) {
        setSaving(false);
        return setError('La réduction doit être comprise entre 0 et 99%.');
      }

      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();

      formDataToSend.append('nom', formData.nom);
      formDataToSend.append('description', formData.description);
  formDataToSend.append('prix', prixNumber);
      formDataToSend.append('categorie', formData.categorie);
  formDataToSend.append('pourcentageReduction', reduc || 0);

      if (formData.materiaux) {
        const materiauxArray = formData.materiaux.split(',').map(m => m.trim());
        materiauxArray.forEach(mat => formDataToSend.append('materiaux[]', mat));
      }

      if (formData.dimensions.longueur) {
        formDataToSend.append('dimensions', JSON.stringify(formData.dimensions));
      }

      photos.forEach(photo => {
        formDataToSend.append('photos', photo);
      });

      if (editingProduct) {
        await axios.put(`/produits/${editingProduct._id}`, formDataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setSuccess('Produit modifié avec succès !');
      } else {
        await axios.post('/produits', formDataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setSuccess('Produit ajouté avec succès !');
      }

      resetForm();
      await fetchProduits();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement du produit');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (produit) => {
    setEditingProduct(produit);
    setFormData({
      nom: produit.nom,
      description: produit.description,
      prix: produit.prix,
      categorie: produit.categorie,
      materiaux: produit.materiaux?.join(', ') || '',
      dimensions: produit.dimensions || { longueur: '', largeur: '', hauteur: '', unite: 'cm' },
      pourcentageReduction: produit.pourcentageReduction || 0,
    });
    setShowForm(true);
    setPhotosPreviews(produit.photos?.map(p => getImageUrl(p)) || []);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/produits/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Produit supprimé avec succès');
      await fetchProduits();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <i className="fas fa-spinner fa-spin text-4xl text-accent-500"></i>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold text-primary-900 mb-2">Mes Produits</h1>
            <p className="text-gray-600">{produits.length} produit{produits.length > 1 ? 's' : ''}</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            variant="primary"
            size="lg"
          >
            <i className={`fas fa-${showForm ? 'times' : 'plus'} mr-2`}></i>
            {showForm ? 'Annuler' : 'Ajouter un produit'}
          </Button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-800">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto"><i className="fas fa-times"></i></button>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-800">
            <i className="fas fa-check-circle"></i>
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto"><i className="fas fa-times"></i></button>
          </div>
        )}

        {/* Formulaire */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-display font-bold text-primary-900 mb-6">
              {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom du produit <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                    placeholder="Ex: Panier en vannerie traditionnelle"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500 resize-none"
                    placeholder="Décrivez votre produit, le savoir-faire, les techniques utilisées..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Prix (F CFA) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="prix"
                    value={formData.prix}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Réduction (%)
                  </label>
                  <input
                    type="number"
                    name="pourcentageReduction"
                    value={formData.pourcentageReduction}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="categorie"
                    value={formData.categorie}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                  >
                    <option value="">Sélectionner...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Matériaux (séparés par des virgules)
                  </label>
                  <input
                    type="text"
                    name="materiaux"
                    value={formData.materiaux}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                    placeholder="Ex: Bois, Cuir, Laine"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Dimensions</label>
                  <div className="grid grid-cols-4 gap-3">
                    <input
                      type="number"
                      name="dimensions.longueur"
                      value={formData.dimensions.longueur}
                      onChange={handleInputChange}
                      placeholder="Longueur"
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                    />
                    <input
                      type="number"
                      name="dimensions.largeur"
                      value={formData.dimensions.largeur}
                      onChange={handleInputChange}
                      placeholder="Largeur"
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                    />
                    <input
                      type="number"
                      name="dimensions.hauteur"
                      value={formData.dimensions.hauteur}
                      onChange={handleInputChange}
                      placeholder="Hauteur"
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                    />
                    <select
                      name="dimensions.unite"
                      value={formData.dimensions.unite}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                    >
                      <option value="cm">cm</option>
                      <option value="m">m</option>
                    </select>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Photos (max 5) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotosChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500"
                  />
                  {photosPreviews.length > 0 && (
                    <div className="flex gap-3 mt-4 flex-wrap">
                      {photosPreviews.map((preview, index) => (
                        <div key={index} className="relative w-24 h-24">
                          <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          >
                            <i className="fas fa-times text-xs"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4 pt-6 border-t">
                <Button type="submit" variant="primary" size="lg" disabled={saving}>
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingProduct ? 'Modifier' : 'Ajouter'} le produit
                    </>
                  )}
                </Button>
                <Button type="button" onClick={resetForm} variant="ghost" size="lg" className="border border-gray-300">
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des produits */}
        {loading ? (
          <div className="flex justify-center py-20">
            <i className="fas fa-spinner fa-spin text-5xl text-accent-500"></i>
          </div>
        ) : produits.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow">
            <i className="fas fa-box text-5xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun produit</h3>
            <p className="text-gray-500 mb-6">Commencez par ajouter votre premier produit</p>
            <Button onClick={() => setShowForm(true)} variant="primary" size="lg">
              <i className="fas fa-plus mr-2"></i>
              Ajouter un produit
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
            {produits.map(produit => (
              <div key={produit._id} className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-2xl transition-shadow">
                <div className="relative h-48 overflow-hidden bg-gray-200">
                  <img
                    src={getImageUrl(produit.photos?.[0])}
                    alt={produit.nom}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => { e.target.src = '/images/placeholder-product.svg'; }}
                  />
                  {produit.pourcentageReduction > 0 && (
                    <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded-full text-sm font-bold">
                      -{produit.pourcentageReduction}%
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-display text-lg font-semibold text-primary-900 mb-2 line-clamp-2">{produit.nom}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{produit.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-accent-600">{produit.prix}F CFA</span>
                    <span className="text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded">{produit.categorie}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleEdit(produit)} variant="secondary" size="sm" className="flex-1">
                      <i className="fas fa-edit mr-2"></i>
                      Modifier
                    </Button>
                    <Button onClick={() => handleDelete(produit._id)} variant="danger" size="sm">
                      <i className="fas fa-trash"></i>
                    </Button>
                    <Link to={`/produits/${produit._id}`}>
                      <Button variant="ghost" size="sm">
                        <i className="fas fa-eye"></i>
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MesProduits;
